/**
 * Calendario laboral de Guatemala: zona horaria del negocio, asuetos
 * nacionales (Código de Trabajo, art. 127) y cálculo de días hábiles para
 * mover vencimientos que caen en día inhábil.
 */
import { addDays, dayOfWeek, isoDate } from './iso-date.js';
/**
 * Zona horaria del negocio por defecto. Guatemala es UTC-6 todo el año (sin
 * horario de verano). Cada organización puede cambiarla en su configuración.
 */
export const DEFAULT_TIME_ZONE = 'America/Guatemala';
/** Configuración regional cuyo formato de fecha es `YYYY-MM-DD` (Canadá, inglés). */
const ISO_FORMAT_LOCALE = 'en-CA';
/** Longitud del año (`YYYY`) al inicio de una `IsoDate`. */
const YEAR_PREFIX_LENGTH = 4;
/** Día de la semana del domingo según `dayOfWeek`. */
const SUNDAY = 0;
/**
 * Días inhábiles de la semana por defecto: solo domingo, porque los mercados
 * rurales abren los sábados y se cobra ese día.
 */
const DEFAULT_CLOSED_WEEKDAYS = [SUNDAY];
/**
 * Máximo de días consecutivos que se buscan para hallar un día hábil. Una
 * racha real de días cerrados no pasa de un par de semanas; el tope protege
 * contra un calendario mal configurado (p. ej. todos los días cerrados).
 */
const MAX_BUSINESS_DAY_SEARCH = 31;
/** Formateadores por zona horaria (crear `Intl.DateTimeFormat` es costoso). */
const dateFormatters = new Map();
/** Formateador `YYYY-MM-DD` para la zona horaria indicada (cacheado). */
function dateFormatterFor(timeZone) {
    let formatter = dateFormatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(ISO_FORMAT_LOCALE, { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
        dateFormatters.set(timeZone, formatter);
    }
    return formatter;
}
/** Indica si el runtime reconoce el nombre IANA de la zona horaria. */
export function isValidTimeZone(timeZone) {
    try {
        dateFormatterFor(timeZone);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Fecha de negocio de la organización para un instante (por defecto, ahora).
 * Usar esto — nunca `new Date().toISOString()` — para decidir qué día es "hoy".
 */
export function businessToday(timeZone = DEFAULT_TIME_ZONE, now = new Date()) {
    return isoDate(dateFormatterFor(timeZone).format(now));
}
/**
 * Domingo de Pascua de un año (algoritmo gregoriano anónimo / Meeus).
 * Las constantes numéricas son parte de la fórmula publicada y no tienen
 * significado individual, por eso no se nombran.
 */
export function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return isoDate(`${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}
/**
 * Asuetos de fecha fija como `MM-DD` (Código de Trabajo, art. 127). El 24 y
 * el 31 de diciembre son medios días y no mueven vencimientos, por eso no
 * figuran aquí.
 */
export const FIXED_NATIONAL_HOLIDAYS = [
    { monthDay: '01-01', name: 'Año Nuevo' },
    { monthDay: '05-01', name: 'Día del Trabajo' },
    { monthDay: '06-30', name: 'Día del Ejército' },
    { monthDay: '09-15', name: 'Día de la Independencia' },
    { monthDay: '10-20', name: 'Día de la Revolución' },
    { monthDay: '11-01', name: 'Día de Todos los Santos' },
    { monthDay: '12-25', name: 'Navidad' },
];
/**
 * Asuetos de Semana Santa, relativos al domingo de Pascua (desplazamiento en
 * días; negativo = antes de Pascua).
 */
export const EASTER_RELATIVE_HOLIDAYS = [
    { offsetDays: -3, name: 'Jueves Santo' },
    { offsetDays: -2, name: 'Viernes Santo' },
    { offsetDays: -1, name: 'Sábado Santo' },
];
/**
 * Asuetos nacionales de día completo según el art. 127 del Código de
 * Trabajo, en orden cronológico. La "festividad de la localidad" (feria
 * patronal) varía por municipio, así que cada agencia configura la suya en
 * `BusinessCalendar.extraHolidays`.
 */
export function guatemalaHolidays(year) {
    const y = String(year);
    const easter = easterSunday(year);
    const fixed = FIXED_NATIONAL_HOLIDAYS.map((h) => ({ date: isoDate(`${y}-${h.monthDay}`), name: h.name }));
    const movable = EASTER_RELATIVE_HOLIDAYS.map((h) => ({ date: addDays(easter, h.offsetDays), name: h.name }));
    // Semana Santa siempre cae entre marzo y abril: después de Año Nuevo y antes del 1 de mayo.
    const [newYear, ...restOfYear] = fixed;
    return newYear ? [newYear, ...movable, ...restOfYear] : movable;
}
/** Conjuntos de asuetos nacionales por año (se calculan una sola vez). */
const holidayCache = new Map();
/** Asuetos nacionales del año como conjunto para búsquedas O(1). */
function nationalHolidaySet(year) {
    let set = holidayCache.get(year);
    if (!set) {
        set = new Set(guatemalaHolidays(year).map((h) => h.date));
        holidayCache.set(year, set);
    }
    return set;
}
/** Indica si la fecha es hábil: no es día cerrado, ni asueto nacional, ni feriado local. */
export function isBusinessDay(value, calendar = {}) {
    const closedWeekdays = calendar.closedWeekdays ?? DEFAULT_CLOSED_WEEKDAYS;
    if (closedWeekdays.includes(dayOfWeek(value)))
        return false;
    if (nationalHolidaySet(Number(value.slice(0, YEAR_PREFIX_LENGTH))).has(value))
        return false;
    return !(calendar.extraHolidays ?? []).includes(value);
}
/** La misma fecha si es hábil; si no, el siguiente día hábil. */
export function nextBusinessDay(value, calendar = {}) {
    let candidate = value;
    for (let i = 0; i < MAX_BUSINESS_DAY_SEARCH; i++) {
        if (isBusinessDay(candidate, calendar))
            return candidate;
        candidate = addDays(candidate, 1);
    }
    throw new RangeError(`El calendario no tiene días hábiles en ${String(MAX_BUSINESS_DAY_SEARCH)} días consecutivos`);
}
//# sourceMappingURL=guatemala.js.map